// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { NotifyProps } from "@bg/App/Components/Notify";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface MiniCartOverlayStyles {
    miniCartContainerStyles?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    miniCartHeadingStyles?: InjectableCss;
    msgWrapper?: InjectableCss;
    notificationContainers?: NotifyProps;
    miniCartHeadingTotalStyles?: InjectableCss;
    cartLinesHeadingStyles?: InjectableCss;
    fancyOverflowStyles?: InjectableCss;
    cartLineStyles?: InjectableCss;
    cartLineRightSideStyles?: InjectableCss;
    cartLineRemoveButtonStyles?: InjectableCss;
    cartLineImageStyles?: InjectableCss;
    cartLineDescriptionNumbersWrapper?: InjectableCss;
    cartLineDescriptionStyles?: InjectableCss;
    cartLineNumbersStyles?: InjectableCss;
    cartLineLeftSideStyles?: InjectableCss;
    cartLineLeftSideStylesWrapper?: InjectableCss;
    cartLineOrderedQtyWrapper?: InjectableCss;
    cartLinePriceTotalWrapper?: InjectableCss;
    minicartActionButtonsStyles?: InjectableCss;
    minicartCheckoutButton?: InjectableCss;
    minicartQuoteButton?: InjectableCss;
    submitQuoteButton?: LinkPresentationProps;
    subTotalText?: TypographyProps;
}
export const minicartStyles: MiniCartOverlayStyles = {
    miniCartContainerStyles: {
        css: css`
            width: 520px;
            height: 100%;
            min-height: 200px;
            overflow: hidden;
            position: relative;
            display: flex;
            flex-direction: column;
            background: ${getColor("common.ui4")};
        `,
    },
    miniCartHeadingStyles: {
        css: css`
            padding: 0 40px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: white;
            border-bottom: 1px solid ${getColor("common.ui3")};
            margin-bottom: 4px;

            && .total-items {
                display: flex;
                padding: 7px 2px;
                align-items: center;
                justify-content: center;
                background: #75757a;
                color: white;
                border-radius: 4px;
                min-width: 37px;
                max-height: 32px;
            }
        `,
    },
    miniCartHeadingTotalStyles: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        `,
    },
    msgWrapper: {
        css: css`
            width: 100%;
            margin: 10px 0;
        `,
    },
    notificationContainers: {
        notifyContainerOverrides: css`
            max-width: 100%;
        `,
    },
    fancyOverflowStyles: {
        css: css`
            flex-grow: 1;
            padding: 0 5px 0 0;
            overflow: hidden;
            background: white;
            box-sizing: border-box;

            .inner-wrapper {
                overflow: hidden;
                overflow-y: auto;
                padding: 0 35px;
                height: 100%;
                ${styledScrollbar(5)}
            }

            &.empty-cart {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    spinnerWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: ${getColor("primary.contrast")};
                opacity: 0.75;
                z-index: 1;
            }

            & ${/* sc-selector */ LoadingSpinnerStyle} {
                z-index: 1300;
            }
        `,
    },
    cartLineStyles: {
        css: css`
            width: 100%;
            padding: 10px 0;
            position: relative;
            display: flex;
            border-bottom: 1px solid ${getColor("common.ui3")};

            &:last-child {
                border-bottom: 0;
            }
        `,
    },
    cartLineRightSideStyles: {
        css: css`
            display: flex;
            width: 114px;
            height: 114px;
            & div {
                height: 114px;
                width: 114px;
            }
            & img {
                max-height: 114px;
                max-width: 114px;
                overflow: hidden;
                margin: auto;
            }
        `,
    },
    cartLineImageStyles: {
        css: css`
            & div {
                width: 100%;
            }
            width: 100%;
        `,
    },
    cartLineRemoveButtonStyles: {
        css: css`
            position: absolute;
            right: -2px;
            top: 14px;
            padding: 0;
            height: 20;
            outline: 0;
            background: transparent !important;
            border: 0;
            min-height: auto;
            &&:focus {
                box-shadow: none;
            }
            & svg {
                g {
                    fill: ${getColor("secondary.main")};
                }
            }

            &&:focus,
            &&:hover {
                svg g {
                    fill: ${getColor("primary.main")};
                }
            }
        `,
    },
    cartLineDescriptionNumbersWrapper: {
        css: css`
            width: calc(100% - 90px);
            padding-right: 10px;
        `,
    },
    cartLineDescriptionStyles: {
        css: css`
            display: inline-block;
            color: ${getColor("secondary.main")};
            padding-right: 15px;
            height: 44px;
            width: 100%;
            margin-bottom: ${fontConfig.spacing.xSmallHeading};
            &:hover,
            &:focus {
                text-decoration: underline;
                outline: 0;
            }
        `,
    },
    cartLineNumbersStyles: {
        css: css`
            & div {
                font-size: ${fontConfig.fontSize.small}px;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            }
            & strong,
            & span {
                font-size: ${fontConfig.fontSize.xxSmall}px;
                line-height: ${fontConfig.lineHeight.singleLinexXSmall};
            }
        `,
    },
    cartLineLeftSideStyles: {
        css: css`
            width: calc(100% - 134px);
            margin-left: 20px;

            & .error-message {
                margin-top: 5px;
                font-size: ${fontConfig.fontSize.xSmall}px;
            }
        `,
    },
    cartLineOrderedQtyWrapper: {
        css: css`
            display: flex;
            button {
                width: auto;
                background: ${getColor("common.ui4")} !important;
                padding: 0;
                margin: 0 !important;
                &:focus,
                &:active {
                    box-shadow: none !important;
                    border-color: transparent !important;
                }
                &:disabled {
                    background: ${getColor("common.ui4")} !important;
                    opacity: 0.5;
                }
            }

            button:first-child {
                padding-left: 10px;
                padding-right: 5px;
                border-radius: 4px 0 0 4px;
            }

            button:last-child {
                padding-right: 10px;
                padding-left: 5px;
                border-radius: 0 4px 4px 0;
            }

            .qty * {
                margin-top: 0 !important;
                align-items: center;
            }
            input {
                max-width: 56px;
                height: 32px !important;
                text-align: center;
                padding: 0 !important;
                width: auto !important;
                border-radius: 0 !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
            }

            input:focus {
                border: 1px solid ${getColor("secondary.main")} !important;
            }
        `,
    },
    cartLinePriceTotalWrapper: {
        css: css`
            width: calc(100% - 83px);
            display: flex;
            justify-content: space-between;

            & span {
                width: 50%;
                font-size: ${fontConfig.fontSize.xxSmall}px;
            }

            & span.total {
                padding-left: 16px;
                word-break: break-word;
            }

            & span.quote {
                width: 100%;
                text-align: center;
                font-size: ${fontConfig.fontSize.small}px;
                padding-right: 30px;
            }
        `,
    },
    minicartActionButtonsStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            background: ${getColor("common.white")};
            box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
            width: 100%;
            padding: 30px 40px;
            flex: 0 0 auto;
            > div {
                display: flex;
                align-items: center;
                justify-content: space-between;
                & div,
                & a {
                    width: calc(50% - 10px);
                }
                button {
                    width: 100%;
                }
                a {
                    justify-content: flex-end;

                    span:not(:first-child) {
                        margin-right: 0;
                    }
                }
            }

            & .signInToCheckout {
                text-align: center;
                margin-top: 20px;
                margin-bottom: 0px;
            }
        `,
    },
    subTotalText: {
        size: fontConfig.fontSize.large,
        lineHeight: fontConfig.lineHeight.small,
        as: "span",
        css: css`
            word-break: break-all;
        `,
    },
    submitQuoteButton: linkIconStyleGenerator(
        "Info",
        `${fontConfig.fontSize.xSmall}`,
        14,
        "",
        "",
        "margin-bottom: 10px; justify-content: center;",
    ),
};
const CartStyles = minicartStyles;

export default CartStyles;
